// Turns an untrusted "return to" value (usually `location.state.from`, set by
// `PrivateRoute` when it sends a visitor to /login) into a path that is safe
// to hand to `<Redirect>` / `history.push`, or `null` when it is not.
//
// Only same-origin, absolute paths are accepted: the value must start with a
// single "/" and must not contain "\", "@", whitespace or control characters
// (browsers strip tabs/newlines, so "/\t/evil.com" would become
// "//evil.com"). Anything with a scheme or a host ("//evil.com",
// "https://x", "javascript:...") is rejected because it cannot start with a
// single "/".
//
// The result is `pathname + search`. The hash is intentionally dropped, also
// when it is smuggled inside the `pathname` or `search` of a location object:
// no route relies on it, and keeping the return target minimal reduces what
// an attacker-controlled value can carry.
//
// history 4 runs `decodeURI` on the pathname of every location it creates
// (and throws a URIError, crashing the render, when that fails). `<Redirect>`
// creates two: `createLocation(to)` for its target, then
// `history.replace(location)` with that object, so the pathname is decoded
// twice. The validator therefore decodes the pathname until it stops
// changing, for at most MAX_DECODE_ROUNDS rounds, and rejects the value when
// any round fails ("/app/meetings/%", "/app/meetings/%25"), produces
// something unsafe ("/%5Cevil.com" -> "/\evil.com", "/%2509/evil.com" ->
// "/%09/evil.com" -> "/<TAB>/evil.com") or is still changing after the last
// round. Only the pathname is decoded, because history leaves the search
// untouched. The raw value is returned, not a decoded one: every decoded
// form history can produce from it has been checked, while a decoded value
// would be decoded again by history into a form that was not.
const UNSAFE_CHARACTERS = /[\\@\s]/;
const MAX_DECODE_ROUNDS = 3;

// A loop instead of /[\x00-\x1f\x7f]/, which the CRA eslint config flags
// (no-control-regex).
function hasControlCharacter(value) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);

    if (code < 0x20 || code === 0x7f) {
      return true;
    }
  }

  return false;
}

function withoutHash(path) {
  return path.split('#')[0];
}

function toPathAndSearch(from) {
  if (typeof from === 'string') {
    return withoutHash(from);
  }

  if (!from || typeof from !== 'object' || Array.isArray(from)) {
    return null;
  }

  const { pathname, search = '' } = from;

  if (typeof pathname !== 'string' || typeof search !== 'string') {
    return null;
  }

  if (search !== '' && !search.startsWith('?')) {
    return null;
  }

  return withoutHash(pathname + search);
}

function isSafeValue(value) {
  if (!value.startsWith('/') || value.startsWith('//')) {
    return false;
  }

  return !UNSAFE_CHARACTERS.test(value) && !hasControlCharacter(value);
}

// Decodes the pathname the way history does, round after round, and tells
// whether every decoded form is safe and the pathname settles within
// MAX_DECODE_ROUNDS rounds. The path has no hash at this point.
function hasSafeDecodedForms(path) {
  let current = path.split('?')[0];

  for (let round = 0; round < MAX_DECODE_ROUNDS; round += 1) {
    let decoded;

    try {
      decoded = decodeURI(current);
    } catch (error) {
      return false;
    }

    if (!isSafeValue(decoded)) {
      return false;
    }

    if (decoded === current) {
      return true;
    }

    current = decoded;
  }

  return false;
}

export function getSafeInternalPath(from) {
  const path = toPathAndSearch(from);

  if (typeof path !== 'string' || !isSafeValue(path) || !hasSafeDecodedForms(path)) {
    return null;
  }

  return path;
}
